import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { useParams } from "react-router";
import { shallowEqual, useSelector } from "react-redux";

/**
 * Loader/hand-off page for "App Sign-in as Customer". Opened in a new tab from
 * the order actions menu: it asks the backend for a short-lived deep link for
 * this order's customer, then presents a QR code and an "Open in App" button.
 *
 * Opening the app is done by a user tap (the button) or a QR scan — the
 * reliable triggers for a Universal / App Link. The page is identical on mobile
 * and desktop, so a desktop user can scan with the phone that has the app.
 *
 * Restricted to super-admin: other roles (including via a direct URL) see a
 * "not authorised" message and no link is requested. The backend enforces the
 * same restriction on the endpoint.
 *
 * @returns {JSX.Element}
 */
export default function AppSigninAsCustomer() {
  const { orderId } = useParams();
  const [state, setState] = useState({ status: "loading", url: "", error: "" });
  const [copied, setCopied] = useState(false);
  const { role } = useSelector(
    (state) => ({
      role: state.auth?.user?.roles,
    }),
    shallowEqual
  );
  const isSuperAdmin = role && role?.length && role[0] === "super-admin";

  const generate = useCallback(async () => {
    setState({ status: "loading", url: "", error: "" });
    try {
      const res = await axios.post(
        `${import.meta.env.REACT_APP_SITE_URL}order/${orderId}/app-signin-link`
      );
      const url = res?.data?.data?.url || "";
      if (!url) throw new Error("No link returned");
      setState({ status: "ready", url, error: "" });
    } catch (e) {
      const message =
        e?.response?.data?.message || "Could not generate the sign-in link.";
      setState({ status: "error", url: "", error: message });
    }
  }, [orderId]);

  useEffect(() => {
    if (isSuperAdmin) {
      generate();
    }
  }, [generate, isSuperAdmin]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(state.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the link is still selectable in the field */
    }
  };

  if (!isSuperAdmin) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Not authorised</h1>
          <p style={styles.hint}>
            You do not have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Sign in to the app as this customer</h1>

        {state.status === "loading" && (
          <p style={styles.hint}>Generating a secure sign-in link…</p>
        )}

        {state.status === "error" && (
          <>
            <p style={styles.error}>{state.error}</p>
            <button style={styles.primaryBtn} onClick={generate}>
              Try again
            </button>
          </>
        )}

        {state.status === "ready" && (
          <>
            <p style={styles.hint}>
              Open this on the device with the easyStorage app installed. The
              link expires shortly.
            </p>
            <div style={styles.qrWrap}>
              <QRCodeSVG value={state.url} size={256} level="L" marginSize={2} />
            </div>
            <a href={state.url} style={styles.primaryBtn}>
              Open in App
            </a>
            <div style={styles.linkRow}>
              <input
                readOnly
                value={state.url}
                style={styles.linkInput}
                onFocus={(e) => e.target.select()}
              />
              <button style={styles.copyBtn} onClick={copyLink}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#F7F7F7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    width: "100%",
    maxWidth: 380,
    background: "#fff",
    borderRadius: 16,
    padding: "32px 24px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  },
  title: { fontSize: 20, fontWeight: 700, color: "#101828", margin: "0 0 8px" },
  hint: { fontSize: 14, color: "#575757", lineHeight: 1.5, margin: "0 0 20px" },
  error: { fontSize: 14, color: "#BE1012", margin: "0 0 16px" },
  qrWrap: {
    display: "inline-flex",
    background: "#fff",
    padding: 12,
    borderRadius: 12,
    border: "1px solid #EAEAEA",
    marginBottom: 20,
  },
  primaryBtn: {
    display: "inline-block",
    background: "#F7801E",
    color: "#fff",
    fontWeight: 600,
    fontSize: 15,
    textDecoration: "none",
    border: "none",
    borderRadius: 10,
    padding: "12px 24px",
    cursor: "pointer",
  },
  linkRow: { display: "flex", gap: 8, marginTop: 20 },
  linkInput: {
    flex: 1,
    fontSize: 12,
    padding: "8px 10px",
    border: "1px solid #EAEAEA",
    borderRadius: 8,
    color: "#575757",
  },
  copyBtn: {
    background: "#F2F2F2",
    border: "1px solid #EAEAEA",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    cursor: "pointer",
  },
};
